import React, { useEffect, useRef, useState, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Dropdown, Button } from 'antd'
import type { MenuProps } from 'antd'
import {
  CloseOutlined,
  DownOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons'
import { useTabbarStore } from '@/stores/tabbar'
import { useFormCacheStore } from '@/stores/form-cache'
import { menuConfig } from '@/lib/menu/config'
import { findMenuByPath } from '@/lib/menu/utils'
import { findDynamicRouteMatch, generateTitle } from '@/lib/tabs/utils'
import type { Tab } from '@/types/menu'

export interface ChromeTabsProps {
  /** 自定义类名 */
  className?: string
}

/**
 * Chrome 风格标签栏组件
 *
 * 特性：
 * - 圆角梯形标签
 * - 平滑的切换动画
 * - 标签溢出省略
 * - 下拉菜单列出所有标签
 * - 右键菜单支持
 */
export function ChromeTabs({ className = '' }: ChromeTabsProps) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const tabsContainerRef = useRef<HTMLDivElement>(null)
  const tabRefsMap = useRef<Map<string, HTMLDivElement>>(new Map())

  // Zustand 状态
  const tabs = useTabbarStore((state) => state.tabs)
  const activeTab = useTabbarStore((state) => state.activeTab)
  const addTab = useTabbarStore((state) => state.addTab)
  const setActiveTab = useTabbarStore((state) => state.setActiveTab)
  const removeTab = useTabbarStore((state) => state.removeTab)
  const removeOtherTabs = useTabbarStore((state) => state.removeOtherTabs)
  const removeLeftSide = useTabbarStore((state) => state.removeLeftSide)
  const removeRightSide = useTabbarStore((state) => state.removeRightSide)

  // 表单缓存
  const clearFormCache = useFormCacheStore((state) => state.clearFormCache)
  const clearFormCaches = useFormCacheStore((state) => state.clearFormCaches)

  // 右键菜单状态
  const [contextMenu, setContextMenu] = useState<{
    visible: boolean
    x: number
    y: number
    tabId: string
  }>({ visible: false, x: 0, y: 0, tabId: '' })

  // 当路由变化时，自动添加或激活标签
  useEffect(() => {
    // 阶段 1: 尝试菜单路由
    const menuItem = findMenuByPath(menuConfig, pathname)
    if (menuItem && menuItem.href) {
      addTab({
        id: menuItem.href,
        title: menuItem.title,
        href: menuItem.href,
        icon: menuItem.icon,
        closable: menuItem.href !== '/dashboard',
        timestamp: Date.now(),
      })
      return
    }

    // 阶段 2: 尝试动态路由
    const dynamicMatch = findDynamicRouteMatch(pathname)
    if (dynamicMatch) {
      const { config, params } = dynamicMatch
      const title = generateTitle(config.titleTemplate, params)

      addTab({
        id: pathname, // 使用完整路径作为唯一 ID
        title, // 生成的动态标题
        href: pathname,
        icon: config.icon,
        closable: true, // 动态路由标签都可关闭
        timestamp: Date.now(),
      })
      return
    }

    // 阶段 3: 未匹配任何配置，不创建标签
    console.debug('[ChromeTabs] No tab created for:', pathname)
  }, [pathname, addTab])

  // 当激活的标签变化时，自动滚动到可视区域
  const scrollActiveTabIntoView = useCallback(() => {
    if (!activeTab || !tabsContainerRef.current) return

    const tabElement = tabRefsMap.current.get(activeTab)
    if (!tabElement) return

    const container = tabsContainerRef.current
    const containerRect = container.getBoundingClientRect()
    const tabRect = tabElement.getBoundingClientRect()

    // 检查标签是否在可视区域外
    const isHiddenLeft = tabRect.left < containerRect.left
    const isHiddenRight = tabRect.right > containerRect.right

    if (isHiddenLeft || isHiddenRight) {
      // 使用 scrollIntoView 平滑滚动
      tabElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center', // 滚动到中间位置
      })
    }
  }, [activeTab])

  // 监听 activeTab 变化，触发滚动
  useEffect(() => {
    // 使用 requestAnimationFrame 确保 DOM 已更新
    const rafId = requestAnimationFrame(() => {
      scrollActiveTabIntoView()
    })
    return () => cancelAnimationFrame(rafId)
  }, [activeTab, scrollActiveTabIntoView])

  // 处理标签点击
  const handleTabClick = (tab: Tab) => {
    setActiveTab(tab.id)
    navigate(tab.href)
  }

  // 处理标签关闭
  const handleTabClose = (e: React.MouseEvent, tabId: string) => {
    e.stopPropagation()
    const tab = tabs.find((t) => t.id === tabId)
    if (!tab || tab.closable === false) return

    // 如果关闭的是当前激活的标签，需要切换到其他标签
    if (tabId === activeTab) {
      const currentIndex = tabs.findIndex((t) => t.id === tabId)
      const nextTab = tabs[currentIndex + 1] || tabs[currentIndex - 1]
      if (nextTab) {
        navigate(nextTab.href)
      }
    }

    clearFormCache(tabId)
    removeTab(tabId)
  }

  // 处理右键菜单
  const handleContextMenu = (e: React.MouseEvent, tabId: string) => {
    e.preventDefault()
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      tabId,
    })
  }

  // 关闭右键菜单
  const closeContextMenu = () => {
    setContextMenu((prev) => ({ ...prev, visible: false }))
  }

  // 右键菜单操作
  const handleCloseTab = () => {
    clearFormCache(contextMenu.tabId)
    removeTab(contextMenu.tabId)
    closeContextMenu()
  }

  const handleCloseOthers = () => {
    const pathsToClose = tabs
      .filter((t) => t.id !== contextMenu.tabId && t.closable !== false)
      .map((t) => t.href)
    clearFormCaches(pathsToClose)
    removeOtherTabs(contextMenu.tabId)
    closeContextMenu()
  }

  const handleCloseLeftSide = () => {
    const index = tabs.findIndex((t) => t.id === contextMenu.tabId)
    if (index > 0) {
      const pathsToClose = tabs
        .slice(0, index)
        .filter((t) => t.closable !== false)
        .map((t) => t.href)
      clearFormCaches(pathsToClose)
    }
    removeLeftSide(contextMenu.tabId)
    closeContextMenu()
  }

  const handleCloseRightSide = () => {
    const index = tabs.findIndex((t) => t.id === contextMenu.tabId)
    if (index >= 0 && index < tabs.length - 1) {
      const pathsToClose = tabs
        .slice(index + 1)
        .filter((t) => t.closable !== false)
        .map((t) => t.href)
      clearFormCaches(pathsToClose)
    }
    removeRightSide(contextMenu.tabId)
    closeContextMenu()
  }

  // 计算右键菜单项状态
  const contextTabIndex = tabs.findIndex((t) => t.id === contextMenu.tabId)
  const contextTab = tabs.find((t) => t.id === contextMenu.tabId)
  const canClose = contextTab?.closable !== false
  const canCloseOthers = tabs.filter((t) => t.closable !== false).length > 1
  const hasLeftTabs = contextTabIndex > 0 && tabs.slice(0, contextTabIndex).some((t) => t.closable !== false)
  const hasRightTabs =
    contextTabIndex >= 0 &&
    contextTabIndex < tabs.length - 1 &&
    tabs.slice(contextTabIndex + 1).some((t) => t.closable !== false)

  // 点击外部关闭右键菜单
  useEffect(() => {
    const handleClickOutside = () => closeContextMenu()
    if (contextMenu.visible) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [contextMenu.visible])

  // 下拉菜单内容
  const dropdownMenu: MenuProps = {
    items: [
      {
        key: 'header',
        type: 'group',
        label: (
          <div className="px-2 py-1 flex gap-1">
            <Button
              size="small"
              type="text"
              icon={<CloseOutlined />}
              onClick={() => {
                const pathsToClose = tabs
                  .filter((t) => t.id !== activeTab && t.closable !== false)
                  .map((t) => t.href)
                clearFormCaches(pathsToClose)
                removeOtherTabs(activeTab)
              }}
              disabled={tabs.filter((t) => t.closable !== false).length <= 1}
            >
              其他
            </Button>
            <Button
              size="small"
              type="text"
              icon={<LeftOutlined />}
              onClick={() => {
                const index = tabs.findIndex((t) => t.id === activeTab)
                if (index > 0) {
                  const pathsToClose = tabs
                    .slice(0, index)
                    .filter((t) => t.closable !== false)
                    .map((t) => t.href)
                  clearFormCaches(pathsToClose)
                }
                removeLeftSide(activeTab)
              }}
              disabled={
                !tabs.slice(0, tabs.findIndex((t) => t.id === activeTab)).some((t) => t.closable !== false)
              }
            >
              左侧
            </Button>
            <Button
              size="small"
              type="text"
              icon={<RightOutlined />}
              onClick={() => {
                const index = tabs.findIndex((t) => t.id === activeTab)
                if (index >= 0 && index < tabs.length - 1) {
                  const pathsToClose = tabs
                    .slice(index + 1)
                    .filter((t) => t.closable !== false)
                    .map((t) => t.href)
                  clearFormCaches(pathsToClose)
                }
                removeRightSide(activeTab)
              }}
              disabled={
                !tabs
                  .slice(tabs.findIndex((t) => t.id === activeTab) + 1)
                  .some((t) => t.closable !== false)
              }
            >
              右侧
            </Button>
          </div>
        ),
      },
      { type: 'divider' },
      ...tabs.map((tab) => ({
        key: tab.id,
        label: (
          <div className="flex items-center justify-between w-full min-w-[200px]">
            <span className="truncate">{tab.title}</span>
            {tab.id === activeTab && (
              <div className="w-2 h-2 rounded-full bg-blue-600 ml-2" />
            )}
          </div>
        ),
        onClick: () => handleTabClick(tab),
      })),
    ],
  }

  return (
    <>
      <div
        className={`flex items-center h-10 bg-slate-100 border-b border-slate-200 ${className}`}
      >
        {/* 标签列表 */}
        <div
          ref={tabsContainerRef}
          className="flex-1 flex items-end overflow-x-auto scrollbar-hide"
        >
          {tabs.map((tab) => {
            const isActive = tab.id === activeTab
            return (
              <div
                key={tab.id}
                ref={(el) => {
                  if (el) {
                    tabRefsMap.current.set(tab.id, el)
                  } else {
                    tabRefsMap.current.delete(tab.id)
                  }
                }}
                className={`
                  group relative flex items-center h-9 px-4 cursor-pointer select-none
                  transition-all duration-200 ease-out
                  ${
                    isActive
                      ? 'bg-white text-blue-700 font-medium z-10'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200/80'
                  }
                `}
                style={{
                  borderTopLeftRadius: '8px',
                  borderTopRightRadius: '8px',
                  marginRight: '-8px',
                  paddingLeft: '16px',
                  paddingRight: tab.closable !== false ? '8px' : '16px',
                }}
                onClick={() => handleTabClick(tab)}
                onContextMenu={(e) => handleContextMenu(e, tab.id)}
              >
                {/* 左侧 Chrome 风格曲线 - 使用 box-shadow 技术 */}
                {isActive && (
                  <div
                    className="absolute -left-2 bottom-0 w-2 h-2 bg-transparent pointer-events-none"
                    style={{
                      borderBottomRightRadius: '8px',
                      boxShadow: '2px 2px 0 2px white',
                    }}
                  />
                )}

                {/* 标签标题 */}
                <span className="text-sm truncate max-w-[120px]">{tab.title}</span>

                {/* 关闭按钮 */}
                {tab.closable !== false && (
                  <button
                    onClick={(e) => handleTabClose(e, tab.id)}
                    className={`
                      ml-2 p-0.5 rounded-full transition-all duration-150
                      ${isActive ? 'opacity-80' : 'opacity-0 group-hover:opacity-60'}
                      hover:bg-slate-200 hover:!opacity-100
                    `}
                  >
                    <CloseOutlined style={{ fontSize: '10px' }} />
                  </button>
                )}

                {/* 右侧 Chrome 风格曲线 - 使用 box-shadow 技术 */}
                {isActive && (
                  <div
                    className="absolute -right-2 bottom-0 w-2 h-2 bg-transparent pointer-events-none"
                    style={{
                      borderBottomLeftRadius: '8px',
                      boxShadow: '-2px 2px 0 2px white',
                    }}
                  />
                )}
              </div>
            )
          })}
        </div>

        {/* 下拉菜单按钮 */}
        <div className="shrink-0 border-l border-slate-200 bg-slate-100">
          <Dropdown
            menu={dropdownMenu}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              icon={<DownOutlined />}
              className="!h-10 !rounded-none !px-3 hover:!bg-slate-200"
            />
          </Dropdown>
        </div>
      </div>

      {/* 右键菜单 */}
      {contextMenu.visible && (
        <div
          className="fixed z-50 bg-white rounded-lg shadow-lg border border-slate-200 py-1 min-w-[140px]"
          style={{
            left: contextMenu.x,
            top: contextMenu.y,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="w-full px-4 py-2 text-sm text-left hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleCloseTab}
            disabled={!canClose}
          >
            关闭标签
          </button>
          <button
            className="w-full px-4 py-2 text-sm text-left hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleCloseOthers}
            disabled={!canCloseOthers}
          >
            关闭其他
          </button>
          <div className="border-t border-slate-100 my-1" />
          <button
            className="w-full px-4 py-2 text-sm text-left hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleCloseLeftSide}
            disabled={!hasLeftTabs}
          >
            关闭左侧
          </button>
          <button
            className="w-full px-4 py-2 text-sm text-left hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleCloseRightSide}
            disabled={!hasRightTabs}
          >
            关闭右侧
          </button>
        </div>
      )}
    </>
  )
}
